import CalendarTodayIcon from '@mui/icons-material/CalendarToday';
import { Popover } from '@mui/material';
import { DateCalendar, LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterMoment } from '@mui/x-date-pickers/AdapterMoment';
import { PickerSelectionState } from '@mui/x-date-pickers/internals';
import moment, { Moment } from 'moment';
import React, { memo, useEffect, useMemo, useRef, useState } from 'react';
import formattingService from '../../../Services/Formatting/FormattingService';
import useLanguage from '../../../Services/Language/useLanguageHook';
import { EventActionsEnum, EventCategoriesEnum, EventDataTargetsEnum } from '../../../Services/Logging/DataLayerDefinitions';
import loggingService from '../../../Services/Logging/LoggingService';
import trackingService, { TrackingEvent } from '../../../Services/Tracking/TrackingService';
import IconButton from '../Buttons/IconButton';
import InputContainer from './InputContainer';
import styles from './Inputs.module.scss';
import TextInput from './TextInput';

interface IDatePickerInputProps {
    name?: string;
    title?: string;
    value?: Date | null;
    required?: boolean;
    denoteRequired?: boolean;
    handleDateChange: (date: Date | undefined) => void;
    oldestAllowedDate?: Date;
    mostRecentAllowedDate?: Date;
    testId?: string;
    onValidityChange?: (isValid: boolean) => void;
    errorMessage?: string;
    validation?: (value: string) => boolean;
    track: TrackingEvent;
}

const DatePickerInput: React.FC<IDatePickerInputProps> = (props: IDatePickerInputProps) => {
    const [translations, , language] = useLanguage();
    const anchorRef = useRef(null);
    const acceptedDateFormat = moment.localeData().longDateFormat('L');
    const [textInputValue, setTextInputValue] = useState<string | null>(props.value ? formattingService.toShortDayMonthYear(props.value) : null);
    const [isCalendarOpen, setIsCalendarOpen] = useState(false);

    const isMomentAcceptedValue = (value: string) => {
        if (!value || value.trim() === '') {
            return !props.required;
        }

        const valueAsDate = moment(value, acceptedDateFormat, true);
        return valueAsDate.isValid() && (!props.oldestAllowedDate || moment(props.oldestAllowedDate).isSameOrBefore(valueAsDate));
    };

    const handleDateChange = (newValue: Moment | string | null, selectionState?: PickerSelectionState) => {
        const dateTime = newValue ? new Date(newValue as string) : null;
        const momentDate = formattingService.toShortDayMonthYear(dateTime);

        setTextInputValue(momentDate);
        props.handleDateChange(dateTime ?? undefined);
        loggingService.trackEventWithAnalytics(
            EventActionsEnum.DatePickerInteraction,
            EventCategoriesEnum.DateTimeChanged,
            `${props.name}-${momentDate}`,
            EventDataTargetsEnum.DatePickerInput,
        );
        trackingService.track({ ...props.track, eventValue: momentDate });

        if (selectionState === 'finish') {
            handlePopoverClose();
        }
    };

    const handleTextChange = (value: string) => {
        const momentFormatIsValid = isMomentAcceptedValue(value);
        const momentConvertedDate = moment(value, acceptedDateFormat).toDate();

        if (momentFormatIsValid && !Number.isNaN(momentConvertedDate.getTime())) {
            props.handleDateChange(momentConvertedDate);
        }

        if (value === '') {
            props.handleDateChange(undefined);
        }

        setTextInputValue(value);
    };

    const handlePopoverOpen = () => {
        setIsCalendarOpen(true);
        loggingService.trackEventWithAnalytics(
            EventActionsEnum.DatePickerInteraction,
            EventCategoriesEnum.DateTimePickerOpened,
            props.name,
            EventDataTargetsEnum.DatePickerInput,
        );
    };

    const handlePopoverClose = () => {
        setIsCalendarOpen(false);
        loggingService.trackEventWithAnalytics(
            EventActionsEnum.DatePickerInteraction,
            EventCategoriesEnum.DateTimePickerClosed,
            props.name,
            EventDataTargetsEnum.DatePickerInput,
        );
    };

    // When the page first loads there is sometimes a delay in getting that inital props.value.
    useEffect(() => {
        const formattedPropsValue = formattingService.toShortDayMonthYear(props.value);
        if (formattedPropsValue === textInputValue || !props.value || props.value === null) {
            return;
        }
        if (textInputValue && textInputValue.trim() !== '' && !isMomentAcceptedValue(textInputValue)) {
            return;
        }
        setTextInputValue(formattedPropsValue);
    }, [props.value]);

    useEffect(() => {
        if (props.onValidityChange) {
            const isValid = isMomentAcceptedValue(textInputValue || '');
            props.onValidityChange(isValid);
        }
    }, [textInputValue, props.required]);

    const value = useMemo(() => {
        if (props.value !== null && props.value !== undefined) {
            return moment(props.value);
        }
        return null;
    }, [props.value]);

    const effectiveErrorMessage = useMemo(() => {
        if (props.errorMessage) {
            return props.errorMessage;
        }

        if (props.oldestAllowedDate) {
            return `${translations.errors.dateFormatWithOldestDateError} ${formattingService.toShortDayMonthYear(props.oldestAllowedDate)}.`;
        }

        return translations.errors.dateFormatError;
    }, [props.errorMessage, props.oldestAllowedDate]);

    return (
        <InputContainer title={props.denoteRequired && props.title ? `${props.title} *` : props.title}>
            <LocalizationProvider dateAdapter={AdapterMoment} adapterLocale={language}>
                <div className={styles.date_picker}>
                    <TextInput
                        testId={props.testId}
                        required={props.required}
                        onValidityChange={props.onValidityChange}
                        validation={props.validation || isMomentAcceptedValue}
                        placeholder={acceptedDateFormat}
                        handleTextChange={handleTextChange}
                        value={textInputValue}
                        errorMessage={effectiveErrorMessage}
                        track={{ eventCategory: props.track.eventCategory, eventAction: `${props.track.eventAction}` }}
                    />
                    <div className={styles.date_picker_icon_button}>
                        <IconButton
                            reference={anchorRef}
                            handleClick={handlePopoverOpen}
                            track={{ eventCategory: props.track.eventCategory, eventAction: `${props.track.eventAction}PopoverOpenClicked` }}
                        >
                            <CalendarTodayIcon />
                        </IconButton>
                        <Popover
                            open={isCalendarOpen}
                            anchorEl={anchorRef.current}
                            onClose={handlePopoverClose}
                            anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
                            transformOrigin={{ vertical: 'top', horizontal: 'left' }}
                        >
                            <DateCalendar
                                className={styles.date_picker_calendar}
                                value={value}
                                onChange={handleDateChange}
                                minDate={moment(props.oldestAllowedDate ?? new Date(0))}
                                maxDate={props.mostRecentAllowedDate ? moment(props.mostRecentAllowedDate) : undefined}
                            />
                        </Popover>
                    </div>
                </div>
            </LocalizationProvider>
        </InputContainer>
    );
};

export default memo(DatePickerInput);
